/**
 * Sponsor Discovery view - declarative React component.
 * 
 * Elm-ish architecture: This component is THIN.
 * - No business logic
 * - No state mutations
 * - No API calls
 * - Only renders based on model state
 * - Delegates all actions to callbacks from the program
 */
import { Link } from 'react-router-dom';
import { RoutePaths } from '@/routes';
import type { SponsorDiscoveryModel, DiscoveryCard } from './model';
import { canRequestIntro, getIntroDisabledReasonText } from './model';
import type { UseSponsorDiscoveryProgramResult } from './useSponsorDiscoveryProgram';

/**
 * Props for the view component.
 */
export interface SponsorDiscoveryViewProps {
  /** Current model state */
  model: SponsorDiscoveryModel;
  /** Skip the current card */
  onSkip: () => void;
  /** Save/like the current card */
  onSave: () => void;
  /** Request an intro for the current card */
  onRequestIntro: () => void;
  /** Retry after an error */
  onRetry: () => void;
  /** Load more profiles */
  onLoadMore: () => void;
  /** Whether the current user is a sponsor (affects empty state copy) */
  isSponsor?: boolean;
  /** FIX: Whether the current user is a friend (affects Intro button disabled state) */
  isFriend?: boolean;
}

/**
 * Privacy/safety notice for profile discovery.
 * Styled as a quiet, pill-style banner that doesn't compete with the card.
 */
function PrivacyNotice() {
  return (
    <div className="privacy-notice" data-testid="privacy-notice">
      <span className="privacy-icon">🔒</span>
      <p className="notice-text">
        Identity-minimized profiles. No contact details or sensitive info shared.
      </p>
    </div>
  );
}

/**
 * Loading state for profile list.
 */
export function LoadingState() {
  return (
    <div className="profile-list-loading" data-testid="profile-list-loading">
      <p>Loading profiles...</p>
    </div>
  );
}

/**
 * Error state for profile list with retry option.
 */
export function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="profile-list-error" data-testid="profile-list-error">
      <p className="error-text">{message}</p>
      <p className="error-hint">Check back later for new profiles.</p>
      <button className="retry-button" onClick={onRetry} data-testid="retry-button">
        Try Again
      </button>
    </div>
  );
}

/**
 * Empty state when no profiles available.
 * Context-sensitive, friendly copy based on registration kind.
 */
export function EmptyState({ isSponsor = false }: { isSponsor?: boolean }) {
  return (
    <div className="profile-list-empty" data-testid="profile-list-empty">
      <p className="empty-title">You're all caught up for now.</p>
      <p className="empty-hint">
        {isSponsor 
          ? 'No new friend introductions yet. Check back soon!'
          : 'No sponsor profiles available right now. Check back soon!'}
      </p>
    </div>
  );
}

/**
 * Individual profile card component - list style without actions.
 */
function ProfileCard({ 
  profile
}: { 
  profile: DiscoveryCard;
}) {
  // Badge shows the profile type being discovered (opposite of viewer's type)
  const profileType = profile.profile_type;
  const badgeLabel = profileType === 'friend' ? 'friend' : 'sponsor';
  const displayName = profile.display_name || (profileType === 'friend' ? 'Anonymous Friend' : 'Anonymous Sponsor');
  
  return (
    <div className="profile-card" data-testid={`profile-${profile.id}`}>
      <div className="card-header">
        <h3 className="card-title" data-testid={`profile-title-${profile.id}`}>
          {displayName}
        </h3>
        <span className="card-badge" data-testid={`profile-badge-${profile.id}`}>
          {badgeLabel}
        </span>
      </div>
      
      {profile.headline && (
        <p className="card-headline" data-testid={`profile-headline-${profile.id}`}>
          {profile.headline}
        </p>
      )}
      
      {profile.bio && (
        <p className="card-bio" data-testid={`profile-bio-${profile.id}`}>
          {profile.bio}
        </p>
      )}
      
      {profile.city_or_region && (
        <div className="card-location">
          <span className="location-icon">📍</span>
          <span className="location-value">{profile.city_or_region}</span>
        </div>
      )}
      
      <div className="card-footer">
        <span className="card-updated">
          Updated {formatRelativeTime(profile.updated_at)}
        </span>
      </div>
    </div>
  );
}

/**
 * Active card component - Tinder-style card with action buttons.
 * Shows the card at activeIndex with Skip, Save, and Request Intro actions.
 * 
 * FIX: Intro button uses canRequestIntro helper for correct disabled state.
 * Intro is disabled only for: friend accounts, already requested, or submitting.
 * Intro is NOT disabled due to: remaining profile count, missing location, etc.
 */
function ActiveCard({ 
  card,
  pendingAction,
  error,
  onSkip,
  onSave,
  onRequestIntro,
  requestedIntros,
  isSponsor = false,
  isFriend = false,
}: {
  card: DiscoveryCard;
  pendingAction: import('./model').SponsorDiscoveryAction | null;
  error: string | null;
  onSkip: () => void;
  onSave: () => void;
  onRequestIntro: () => void;
  /** Set of profile IDs that have already had intro requested */
  requestedIntros?: ReadonlySet<string>;
  /** Whether the current user is a sponsor (affects Intro button state) */
  isSponsor?: boolean;
  /** Whether the current user is a friend (affects Intro button state) */
  isFriend?: boolean;
}) {
  const isPending = pendingAction !== null;
  
  // Use canRequestIntro helper for consistent intro button state
  const introCheck = canRequestIntro({
    viewerIsSponsor: isSponsor,
    viewerIsFriend: isFriend,
    profile: card,
    requestedIntros: requestedIntros ?? new Set(),
    hasPendingAction: isPending,
  });
  const introDisabledReasonText = introCheck.disabledReason 
    ? getIntroDisabledReasonText(introCheck.disabledReason) 
    : null;
  
  // Badge shows the profile type being discovered (opposite of viewer's type)
  const profileType = card.profile_type;
  const badgeLabel = profileType === 'friend' ? 'friend' : 'sponsor';
  const displayName = card.display_name || (profileType === 'friend' ? 'Anonymous Friend' : 'Anonymous Sponsor');
  
  return (
    <div className="active-card-container" data-testid="active-card-container">
      <div className="profile-card active-card" data-testid={`profile-${card.id}`}>
        <div className="card-header">
          <h3 className="card-title" data-testid={`profile-title-${card.id}`}>
            {displayName}
          </h3>
          <span className="card-badge" data-testid={`profile-badge-${card.id}`}>
            {badgeLabel}
          </span>
        </div>
        
        {card.headline && (
          <p className="card-headline" data-testid={`profile-headline-${card.id}`}>
            {card.headline}
          </p>
        )}
        
        {card.bio && (
          <p className="card-bio" data-testid={`profile-bio-${card.id}`}>
            {card.bio}
          </p>
        )}
        
        {card.city_or_region && (
          <div className="card-location">
            <span className="location-icon">📍</span>
            <span className="location-value">{card.city_or_region}</span>
          </div>
        )}
        
        <div className="card-footer">
          <span className="card-updated">
            Updated {formatRelativeTime(card.updated_at)}
          </span>
        </div>
      </div>
      
      {/* Action error display */}
      {error && (
        <div className="action-error" data-testid="action-error">
          <p>{error}</p>
        </div>
      )}
      
      {/* Tinder-style action buttons */}
      <div className="card-actions" data-testid="card-actions">
        <button
          className="action-btn skip-btn"
          onClick={onSkip}
          disabled={isPending}
          data-testid="skip-button"
          title="Skip this profile"
        >
          <span className="action-icon">✕</span>
          <span className="action-label">Skip</span>
        </button>
        
        <button
          className="action-btn save-btn"
          onClick={onSave}
          disabled={isPending}
          data-testid="save-button"
          title="Save this profile"
        >
          <span className="action-icon">♥</span>
          <span className="action-label">Save</span>
        </button>
        
        {/* Intro button uses canRequestIntro helper for correct disabled state */}
        <div className="intro-btn-container" data-testid="intro-btn-container">
          <button
            className={`action-btn intro-btn ${!introCheck.canRequest ? 'intro-btn-disabled' : ''}`}
            onClick={onRequestIntro}
            disabled={!introCheck.canRequest}
            data-testid="request-intro-button"
            title={introCheck.disabledReason 
              ? introDisabledReasonText || "Request introduction" 
              : "Request introduction"}
          >
            <span className="action-icon">✉</span>
            <span className="action-label">Intro</span>
          </button>
          {/* Show visible disabled reason when applicable */}
          {introDisabledReasonText && (
            <span className="intro-disabled-reason" data-testid="intro-disabled-reason">
              {introDisabledReasonText}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Format ISO timestamp to relative time.
 */
function formatRelativeTime(isoString: string): string {
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  } catch {
    return 'recently';
  }
}

/**
 * Profile list with load more button.
 */
function ProfileList({ 
  cards, 
  loading,
  hasMore, 
  onLoadMore 
}: { 
  cards: readonly DiscoveryCard[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}) {
  return (
    <div className="profile-list" data-testid="profile-list">
      {cards.map(card => (
        <ProfileCard key={card.id} profile={card} />
      ))}
      
      {hasMore && (
        <button 
          className="load-more-btn"
          onClick={onLoadMore}
          disabled={loading}
          data-testid="load-more-button"
        >
          {loading ? 'Loading...' : 'Load More'}
        </button>
      )}
    </div>
  );
}

/**
 * Ready state - showing the active card with Tinder-style actions.
 * 
 * This is the key difference from list-style: we show ONE card at a time
 * with Skip, Save, and Request Intro actions. The activeIndex tracks
 * which card we're on, and actions advance to the next card.
 */
function ReadyState(props: SponsorDiscoveryViewProps) {
  const { model, onSkip, onSave, onRequestIntro, onLoadMore, isSponsor, isFriend } = props;
  
  // No cards = empty state
  if (model.cards.length === 0) {
    return <EmptyState isSponsor={isSponsor} />;
  }
  
  // Check if activeIndex is valid
  const activeCard = model.cards[model.activeIndex];
  
  // If we've consumed all cards, show empty state
  if (!activeCard) {
    return <EmptyState isSponsor={isSponsor} />;
  }

  return (
    <div className="discovery-container" data-testid="discovery-container">
      {/* Show the active card with actions */}
      <ActiveCard
        card={activeCard}
        pendingAction={model.pendingAction}
        error={model.error}
        onSkip={onSkip}
        onSave={onSave}
        onRequestIntro={onRequestIntro}
        requestedIntros={model.requestedIntros}
        isSponsor={isSponsor}
        isFriend={isFriend}
      />
      
      {/* Show remaining card count */}
      <div className="cards-remaining" data-testid="cards-remaining">
        {model.cards.length - model.activeIndex - 1} more profiles available
      </div>
      
      {/* Load more button for pagination */}
      {model.hasMore && (
        <button 
          className="load-more-btn"
          onClick={onLoadMore}
          disabled={model.status === 'loading'}
          data-testid="load-more-button"
        >
          {model.status === 'loading' ? 'Loading...' : 'Load More'}
        </button>
      )}
    </div>
  );
}

/**
 * Main view component - routes to appropriate sub-component based on status.
 * 
 * This is the ONLY React component in the feature.
 * All logic is in the program hook; this just renders.
 */
export function SponsorDiscoveryView(props: SponsorDiscoveryViewProps) {
  // Show loading state when no cards and idle or loading
  if (props.model.status === 'idle' || (props.model.status === 'loading' && props.model.cards.length === 0)) {
    return <LoadingState />;
  }
  
  // Always show privacy notice when we have content or are in error/empty/ready states
  return (
    <>
      <PrivacyNotice />
      
      {/* Route to appropriate state component */}
      {props.model.status === 'error' && (
        <ErrorState message={props.model.error || 'Unknown error'} onRetry={props.onRetry} />
      )}
      
      {props.model.status === 'empty' && <EmptyState isSponsor={props.isSponsor} />}
      
      {(props.model.status === 'ready' || props.model.status === 'loading') && (
        <ReadyState {...props} />
      )}
    </>
  );
}

